/** Vignette — radial brightness falloff.
 *
 *  Each pixel's distance from the image center, normalized to the
 *  half-diagonal, drives a smooth falloff between the `midpoint`
 *  (where the effect is barely visible) and the corners (where the
 *  effect is fullest). The falloff curve is `smoothstep` —
 *  perceptually smoother than a linear ramp.
 *
 *  Positive `amount` darkens the corners (the classic "lens
 *  vignette" look); negative `amount` brightens them, useful for
 *  removing a real vignette from a photo. */

export interface VignetteValue {
  /** Strength (-100..+100). Negative brightens; positive darkens. */
  amount: number;
  /** Normalized radius where the falloff starts (0..1). 0 = effect
   *  begins at the center (fullest effect); 1 = effect begins only
   *  at the corners (nearly identity). Default ~0.5. */
  midpoint: number;
}

export const identityVignetteValue = (): VignetteValue => ({
  amount: 0,
  midpoint: 0.5,
});

/** Smoothstep: `3t² − 2t³` for t ∈ [0, 1]. Returns 0 outside that
 *  range. Gives a hermite-interpolated falloff that's flat at both
 *  endpoints — visually softer than `t` or `t²`. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  if (x <= edge0) return 0;
  if (x >= edge1) return 1;
  const t = (x - edge0) / (edge1 - edge0);
  return t * t * (3 - 2 * t);
}

export function applyVignettePixels(source: ImageData, value: VignetteValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(-100, Math.min(100, value.amount));
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const midpoint = Math.max(0, Math.min(1, value.midpoint));
  const k = amount / 100; // -1..+1
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / halfDiag;
      const dy = (y - cy) / halfDiag;
      const r = Math.sqrt(dx * dx + dy * dy); // 0..1 (center to corner)
      const t = smoothstep(midpoint, 1, r); // 0..1 falloff factor
      // factor: positive amount → multiplier < 1 in corners (darken)
      //         negative amount → multiplier > 1 in corners (brighten)
      // Linear blend with mid-grey (128) at full effect for negative
      // amounts would clip rapidly; using a multiplicative model in
      // both directions keeps the math symmetric.
      const factor = 1 - k * t;
      const i = (y * w + x) * 4;
      out[i] = src[i] * factor;
      out[i + 1] = src[i + 1] * factor;
      out[i + 2] = src[i + 2] * factor;
      out[i + 3] = src[i + 3];
    }
  }
  return new ImageData(out, w, h);
}

export function applyVignetteToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: VignetteValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyVignettePixels(source, value), 0, 0);
}
